import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import { LoadingSpinner } from './LoadingSpinner';

export const SYSTEM_DOCUMENTATION_PERMISSION = 'system_documentation.read';

interface SystemDocumentationRouteProps {
  children: ReactNode;
}

export function SystemDocumentationRoute({ children }: SystemDocumentationRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const isSystemAdmin = user?.role === 'SystemAdmin';

  const {
    data: roles,
    isLoading: rolesLoading,
    error,
  } = useQuery({
    queryKey: ['system-documentation-permission-check', user?.tenantId],
    queryFn: () => apiClient.getRoles(user!.tenantId, true),
    enabled: !!user?.tenantId && isSystemAdmin,
    staleTime: 300000,
  });

  if (isLoading || (isSystemAdmin && rolesLoading)) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" label="Checking system documentation access..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isSystemAdmin) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400 text-center">
            This documentation is internal and requires elevated access.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center mb-2">Access Check Failed</h2>
          <p className="text-gray-600 dark:text-gray-400 text-center">
            Unable to verify access to system documentation right now.
          </p>
        </div>
      </div>
    );
  }

  const hasSystemDocumentationPermission = roles?.some(
    (role) =>
      role.isSystemRole &&
      role.permissions.some((permission) => permission.code === SYSTEM_DOCUMENTATION_PERMISSION),
  ) ?? false;

  if (!hasSystemDocumentationPermission) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400 text-center">
            Missing required permission: <code>{SYSTEM_DOCUMENTATION_PERMISSION}</code>
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
